// src/components/HRInterviewRoom.jsx
import React, { useEffect, useRef, useState } from"react";
import { useNavigate, useLocation } from"react-router-dom";
import { useTenant } from"../../Context/TenantContext";
import API_BASE_URL from"../../Config/config";
import { trackFeatureUsage } from '../../utils/analyticsTracker';

const TOTAL_INTERVIEW_TIME = 60 * 60 * 1000; // 60 minutes
const MAX_RESPONSE_TIME = 60_000; // 60 seconds per answer

function speak(text) {
 if (!window.speechSynthesis) return;
 window.speechSynthesis.cancel();
 const voices = window.speechSynthesis.getVoices();
 const utterance = new SpeechSynthesisUtterance(text);
 utterance.rate = 1.1;
 utterance.pitch = 1;
 utterance.volume = 1;
 
 // Try to find a better voice if available
 const premiumVoice = voices.find(v => v.name.includes('Google') || v.name.includes('Premium'));
 if (premiumVoice) {
   utterance.voice = premiumVoice;
 } else if (voices.length > 0) {
   utterance.voice = voices[0];
 }

 window.speechSynthesis.speak(utterance);
 return utterance; // Return utterance for onend handling
}

export default function HRInterviewRoom({ mode = "learner" }) {
 const navigate = useNavigate();
 const { state } = useLocation();
 const { getCollegeName, getLogoUrl, tenant } = useTenant();
 const formData = (state && state.formData) || {};

  const isTenantMode = mode === "tenant" || (!!tenant && window.location.hostname !== 'theskillgenie.com' && window.location.hostname !== 'www.theskillgenie.com' && window.location.hostname !== 'localhost');
  const backPath = isTenantMode ? "/student/mockinterviewhome" : "/mockinterviewhome";
  const collegeName = getCollegeName();
  const collegeLogo = getLogoUrl();

 const [audioEnabled, setAudioEnabled] = useState(true);
 const [videoEnabled, setVideoEnabled] = useState(true);
 const [mediaError, setMediaError] = useState(null);
 const [status, setStatus] = useState("idle");
 const [questions, setQuestions] = useState([]);
 const [currentIdx, setCurrentIdx] = useState(0);
 const [qa, setQa] = useState([]);
 const [timers, setTimers] = useState([]);
 const [totalTimeElapsed, setTotalTimeElapsed] = useState(0);
  const videoRef = useRef(null);
  const [videoPos, setVideoPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - videoPos.x,
      y: e.clientY - videoPos.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setVideoPos({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);
 const [voiceIndex, setVoiceIndex] = useState(0); // Track current voice index

 // Reset state when component mounts or formData changes to prevent persisting answers
 useEffect(() => {
 setQa([]);
 setTimers([]);
 setQuestions([]);
 setCurrentIdx(0);
 setTotalTimeElapsed(0);
 setStatus("idle");
 }, [formData.skills, formData.position, formData.jobDescription]);

 const recognitionRef = useRef(null);
 const mediaRecorderRef = useRef(null);
 const recordedChunksRef = useRef([]);
 const localStreamRef = useRef(null);
 const timerIntervalRef = useRef(null);
 const totalTimerRef = useRef(null);
 const responseTimeoutRef = useRef(null);
 const currentAnswerRef = useRef('');
 const currentQuestionRef = useRef(null);
 const manuallyStoppedRef = useRef(false);

 const [errorModal, setErrorModal] = useState({ open: false, message:"" });

 // Offline fallback: richer HR questions when API is unreachable
 const buildLocalHRQuestions = (count = 10) => {
   const skills = Array.isArray(formData.skills) ? formData.skills.filter(Boolean).join(', ') : (formData.skills || "");
   const topic = (formData.jobDescription || formData.position || skills || "your past work");
   const base = [
 `Tell me about yourself and your journey so far.`,
 `Describe a challenging situation at work and how you resolved it.`,
 `Give an example of a time you worked in a team and your specific contribution.`,
 `How do you handle tight deadlines and competing priorities?` ,
 `Describe a situation where you received critical feedback. What did you do?`,
 `What motivates you at work, and what demotivates you?`,
 `Tell me about a time you disagreed with a decision. How did you handle it?`,
 `How do you approach learning new skills or technologies?`,
 `Describe a time you took ownership beyond your role.`,
 `What are your strengths and areas for improvement?`
 ];
 const contextualized = base.map((q) =>
 q.includes("Describe") || q.includes("example")
 ? q.replace(/\.$/, ` related to ${topic}.`)
 : `${q} (context: ${topic})`
 );
 const list = contextualized.concat(contextualized).slice(0, Math.max(1, count));
 return list.map((q, i) => ({ id: i + 1, question: q }));
 };

 // Ensure voices are loaded (some browsers load voices asynchronously)
 useEffect(() => {
 const loadVoices = () => {
 window.speechSynthesis.getVoices(); // Trigger voice loading
 };
 loadVoices();
 window.speechSynthesis.onvoiceschanged = loadVoices;
 return () => {
 window.speechSynthesis.onvoiceschanged = null;
 };
 }, []);

 // Start total timer
 useEffect(() => {
 if (["ready","asking","listening","recording","transcribing"].includes(status)) {
 if (totalTimerRef.current) clearInterval(totalTimerRef.current);
 totalTimerRef.current = setInterval(() => {
 setTotalTimeElapsed(prev => {
 if (prev >= TOTAL_INTERVIEW_TIME) {
 clearInterval(totalTimerRef.current);
 clearInterval(timerIntervalRef.current);
 setStatus("done");
 requestFeedback();
 return prev;
 }
 return prev + 1000;
 });
 }, 1000);
 } else {
 clearInterval(totalTimerRef.current);
 }
 return () => clearInterval(totalTimerRef.current);
 }, [status]);

 // Cleanup on unmount
 useEffect(() => {
 return () => {
 try {
 localStreamRef.current?.getTracks().forEach(t => t.stop());
 } catch {}
 if (recognitionRef.current) {
 try { recognitionRef.current.stop(); } catch {}
 }
 window.speechSynthesis.cancel();
 clearInterval(timerIntervalRef.current);
 clearInterval(totalTimerRef.current);
 clearTimeout(responseTimeoutRef.current);
 };
 }, []);

 const generateQuestions = async () => {
 setStatus("generating");
 try {
 const res = await fetch(`${API_BASE_URL}/ai-services`, {
        method:"POST",
        headers: {
          "Content-Type":"application/json",
          "x-tenant-id": tenant?.tenantId || ""
        },
        body: JSON.stringify({ 
          op:"generate-questions", 
          type:"hr", 
          formData, 
          numQuestions: 10,
          tenantId: tenant?.tenantId 
        }),
      });
 const payload = await res.json();
 if (!payload.ok) throw new Error(payload.error ||"Failed to get HR questions");

 const normalized = Array.isArray(payload.data)
 ? payload.data.map((q, i) =>
 typeof q ==="string"
 ? { id: i + 1, question: q }
 : { id: q.id ?? (i + 1), question: q.question ?? q }
 )
 : [];

 setQuestions(normalized);
 setTimers(normalized.map(() => 0));
 setCurrentIdx(0);
 // Initialize qa array with all questions marked as skipped (empty answer)
 setQa(normalized.map(q => ({ question: q.question, answer: '' })));
 setStatus("ready");

 setTimeout(() => askQuestion(0, normalized), 400);
 } catch (err) {
 // Fallback to locally generated questions if network/API fails
 const local = buildLocalHRQuestions(10);
 if (local.length) {
 setQuestions(local);
 setTimers(local.map(() => 0));
 setCurrentIdx(0);
 // Initialize qa array with all questions marked as skipped (empty answer)
 setQa(local.map(q => ({ question: q.question, answer: '' })));
 setStatus("ready");
 setTimeout(() => askQuestion(0, local), 400);
 } else {
 console.error("Generate HR questions error:", err);
 setErrorModal({ open: true, message:"Error generating HR questions:" + (err.message || err) });
 setStatus("idle");
 }
 }
 };

  const ensureMedia = async () => {
    // Audio is now mandatory
    try {
      if (localStreamRef.current && localStreamRef.current.active) {
        // If stream exists, just ensure the video track state matches preference
        const videoTracks = localStreamRef.current.getVideoTracks();
        if (videoTracks.length > 0) {
          videoTracks[0].enabled = videoEnabled;
        } else if (videoEnabled) {
          // If we need video but don't have a track, we must re-acquire
          localStreamRef.current.getTracks().forEach(t => t.stop());
        } else {
          // We have a stream, no video track, and don't need video
          if (videoRef.current && !videoRef.current.srcObject) {
            videoRef.current.srcObject = localStreamRef.current;
          }
          return true;
        }

        if (videoTracks.length > 0 || !videoEnabled) {
          if (videoRef.current && (!videoRef.current.srcObject || videoRef.current.srcObject !== localStreamRef.current)) {
            videoRef.current.srcObject = localStreamRef.current;
          }
          return true;
        }
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: true, // Always request video to avoid stream restarts when toggling
      });
      localStreamRef.current = stream;

      // Apply initial video preference
      const videoTracks = stream.getVideoTracks();
      if (videoTracks.length > 0) {
        videoTracks[0].enabled = videoEnabled;
      }

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setMediaError(null);
      return true;
    } catch (err) {
      console.error("Media error:", err);
      let message = "Microphone access is required to start the interview.";
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        message = "Permission denied. Audio is mandatory for the interview. Please allow microphone access in your browser settings.";
      } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
        message = "No microphone found. An audio device is required.";
      }
      setMediaError(message);
      setErrorModal({ open: true, message });
      return false;
    }
  };

  const handleLaunch = async () => {
    const hasPermission = await ensureMedia();
    if (hasPermission) {
      generateQuestions();
    }
  };

 const askQuestion = async (idx = currentIdx, qList = questions) => {
 if (!qList.length) return;
 if (idx >= qList.length) {
 setStatus("done");
 await requestFeedback();
 return;
 }

 // Ensure media is active (video/audio should be on from start as per requirement)
 await ensureMedia();

 // Prevent multiple simultaneous speech
 if (window.speechSynthesis?.speaking) {
 window.speechSynthesis.cancel();
 await new Promise(resolve => setTimeout(resolve, 100));
 }

 const qObj = qList[idx];
 setStatus("asking");
 setCurrentIdx(idx);

 const spoken = qObj.question;

 await new Promise(resolve => {
 if (!window.speechSynthesis) return setTimeout(resolve, 1200);
 const utterance = speak(spoken, voiceIndex); // Use current voiceIndex
 const timeout = setTimeout(() => resolve(), 10000); // 10s max wait
 utterance.onend = () => {
 clearTimeout(timeout);
 resolve();
 };
 });

 setStatus("ready");
 };

 const startAnswering = async () => {
 try { window.speechSynthesis.cancel(); } catch {}
 if (window.speechSynthesis?.speaking) {
 await new Promise((resolve) => {
 const check = () => {
 if (!window.speechSynthesis.speaking) return resolve();
 setTimeout(check, 50);
 };
 check();
 });
 }
 await ensureMedia();
 const supportsSR = !!(window.SpeechRecognition || window.webkitSpeechRecognition);
 setStatus(supportsSR ?"listening" :"recording");

 clearInterval(timerIntervalRef.current);
 timerIntervalRef.current = setInterval(() => {
 setTimers(prev => {
 const newTimers = [...prev];
 newTimers[currentIdx] = (newTimers[currentIdx] || 0) + 1;
 return newTimers;
 });
 }, 1000);

 if (supportsSR) {
 await startSpeechRecognition(currentIdx, questions[currentIdx]);
 } else {
 await startRecordingFallback(currentIdx, questions[currentIdx]);
 }
 };

 const stopAnswering = () => {
 // Cancel any ongoing speech synthesis immediately
 try { 
 window.speechSynthesis.cancel(); 
 // Force stop any speaking
 if (window.speechSynthesis.speaking) {
 window.speechSynthesis.cancel();
 }
 } catch {}
 
 clearInterval(timerIntervalRef.current);
 clearTimeout(responseTimeoutRef.current);

 // Save the answer BEFORE stopping recognition
 if (currentQuestionRef.current) {
 const qObj = currentQuestionRef.current;
 const answerToSave = currentAnswerRef.current || '';
 console.log('Saving answer before stopping:', { 
 hasAnswer: !!answerToSave, 
 answer: answerToSave,
 question: qObj.question 
 });
 
 setQa(prev => {
 const existingIdx = prev.findIndex(a => a.question === qObj.question);
 if (existingIdx >= 0) {
 const updated = [...prev];
 updated[existingIdx] = { question: qObj.question, answer: answerToSave };
 console.log('Updated existing answer:', updated[existingIdx]);
 return updated;
 }
 const newQa = [...prev, { question: qObj.question, answer: answerToSave }];
 console.log('Added new answer:', newQa[newQa.length - 1]);
 return newQa;
 });
 }

 // Mark as manually stopped and stop recognition
 manuallyStoppedRef.current = true;
 
 if (recognitionRef.current) {
 try {
 recognitionRef.current.onend = null;
 recognitionRef.current.stop();
 } catch {}
 }
 
 if (mediaRecorderRef.current && mediaRecorderRef.current.state !=="inactive") {
 mediaRecorderRef.current.stop();
 }

 // Move to next question after a short delay to ensure state is updated
 setTimeout(() => {
   const nextIdx = currentIdx + 1;
   // Reset refs
   currentAnswerRef.current = '';
   currentQuestionRef.current = null;
   manuallyStoppedRef.current = false;
   
   if (nextIdx < questions.length) {
     setCurrentIdx(nextIdx);
     askQuestion(nextIdx, questions);
   } else {
     setStatus("done");
     requestFeedback();
   }
 }, 500);
};

 const skipQuestion = () => {
 window.speechSynthesis.cancel();
 clearInterval(timerIntervalRef.current);
 clearTimeout(responseTimeoutRef.current);
 if (recognitionRef.current) {
 try {
 recognitionRef.current.onend = null;
 recognitionRef.current.stop();
 } catch {}
 }
 if (mediaRecorderRef.current && mediaRecorderRef.current.state !=="inactive") {
 mediaRecorderRef.current.stop();
 }

 const q = questions[currentIdx];
 setQa(prev => {
 const updated = [...prev];
 const existingIdx = updated.findIndex(a => a.question === q.question);
 if (existingIdx >= 0) {
 // Save what we have if skipped after answering
 const finalAnswer = currentAnswerRef.current || updated[existingIdx].answer || "";
 updated[existingIdx] = { question: q.question, answer: finalAnswer };
 } else {
 updated.push({ question: q.question, answer: currentAnswerRef.current || "" });
 }
 return updated;
 });
 
 // Reset refs
 currentAnswerRef.current = '';
 currentQuestionRef.current = null;
 
 // Use the updated index value
 const nextIdx = currentIdx + 1;
 setCurrentIdx(nextIdx);
 setStatus("ready");

 if (nextIdx < questions.length) {
 setTimeout(() => askQuestion(nextIdx, questions), 400);
 } else {
 setStatus("done");
 requestFeedback();
 }
};

 const startSpeechRecognition = (idx, qObj) => {
 return new Promise(async (resolve, reject) => {
 try {
 await ensureMedia();
 const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
 if (!SR) throw new Error("SpeechRecognition not supported");

 // Store current question and reset answer
 currentQuestionRef.current = qObj;
 currentAnswerRef.current = '';
 manuallyStoppedRef.current = false;

 const rec = new SR();
 recognitionRef.current = rec;
 rec.lang ="en-US";
 rec.interimResults = true; // Enable interim results for real-time capture
 rec.maxAlternatives = 1;
 rec.continuous = true; // Keep listening until stopped

 rec.onstart = () => setStatus("listening");
 rec.onresult = (ev) => {
 // Capture ALL results (both interim and final) to ensure complete capture
 let fullTranscript = '';
 for (let i = 0; i < ev.results.length; i++) {
 const transcript = ev.results[i][0].transcript;
 fullTranscript += transcript;
 }
 if (fullTranscript) {
 currentAnswerRef.current = fullTranscript;
 console.log('Speech recognition update:', fullTranscript);
 }
 };
 rec.onerror = (e) => {
     recognitionRef.current = null;
     clearInterval(timerIntervalRef.current);
     // Gracefully handle common cases like no-speech, aborted, or network errors
     if (e?.error === 'no-speech' || e?.error === 'aborted' || e?.error === 'audio-capture' || e?.error === 'network') {
         console.warn(`Speech recognition ${e?.error} error, resolving gracefully.`);
         setQa(prev => {
             const existingIdx = prev.findIndex(a => a.question === qObj.question);
             if (existingIdx >= 0) {
                 const updated = [...prev];
                 updated[existingIdx] = { question: qObj.question, answer: currentAnswerRef.current || "" };
                 return updated;
             }
             return [...prev, { question: qObj.question, answer: currentAnswerRef.current || "" }];
         });
         setStatus('ready');
         resolve();
         return;
     }
     setErrorModal({ open: true, message: `Speech recognition error: ${e?.error || 'unknown'}` });
     reject(e);
 };
 rec.onend = () => {
 console.log('Speech recognition ended, status:', status);
 recognitionRef.current = null;

 // If we are still in "listening" status but it ended, it might be the 5s silence timeout
 // In that case, we should restart it unless manually stopped.
 if (status === "listening" && !manuallyStoppedRef.current) {
   console.log('Speech recognition ended prematurely, restarting...');
   try {
     // Small delay to prevent tight loop if it keeps failing
     setTimeout(() => {
       if (status === "listening" && !manuallyStoppedRef.current) {
         try {
           rec.start();
           recognitionRef.current = rec;
         } catch (e) { console.error('Restart failed:', e); }
       }
     }, 300);
     return;
   } catch (err) {
     console.error('Failed to restart speech recognition:', err);
   }
 }

 clearInterval(timerIntervalRef.current);
 // Only save if this wasn't manually stopped
 if (!manuallyStoppedRef.current && currentQuestionRef.current) {
 const qObj = currentQuestionRef.current;
 const answerToSave = currentAnswerRef.current || '';
 setQa(prev => {
 const existingIdx = prev.findIndex(a => a.question === qObj.question);
 if (existingIdx >= 0) {
 const updated = [...prev];
 updated[existingIdx] = { question: qObj.question, answer: answerToSave };
 console.log('Saved from onend:', updated[existingIdx]);
 return updated;
 }
 return [...prev, { question: qObj.question, answer: answerToSave }];
 });
 }
 manuallyStoppedRef.current = false;
 setStatus("ready");
 resolve();
 };

 rec.start();

 responseTimeoutRef.current = setTimeout(() => {
 if (recognitionRef.current) {
 try { recognitionRef.current.stop(); } catch {}
 }
 }, MAX_RESPONSE_TIME);
 } catch (err) {
 reject(err);
 }
 });
 };

 const startRecordingFallback = (idx, qObj) => {
 return new Promise(async (resolve, reject) => {
 try {
 await ensureMedia();
 if (!localStreamRef.current) throw new Error("No media stream");

 // Store current question
 currentQuestionRef.current = qObj;
 currentAnswerRef.current = '';
 recordedChunksRef.current = [];
 
 const mr = new MediaRecorder(localStreamRef.current, { mimeType:"audio/webm" });
 mediaRecorderRef.current = mr;

 mr.ondataavailable = ev => {
 if (ev.data && ev.data.size) recordedChunksRef.current.push(ev.data);
 };
 mr.onstart = () => setStatus("recording");
 mr.onstop = async () => {
 setStatus("transcribing");
 const blob = new Blob(recordedChunksRef.current, { type:"audio/webm" });
 const fd = new FormData();
 fd.append("file", blob,"answer.webm");

 try {
 const r = await fetch(`${API_BASE_URL}/ai-services`, { 
 method:"POST", 
 headers: {"Content-Type":"application/json" },
 body: JSON.stringify({ op:"transcribe" })
 });
 const pl = await r.json();
 if (!pl.ok) throw new Error(pl.error ||"Transcription failed");
 const text = pl.text ||"";
 currentAnswerRef.current = text;
 setQa(prev => {
 const existingIdx = prev.findIndex(a => a.question === qObj.question);
 if (existingIdx >= 0) {
 const updated = [...prev];
 updated[existingIdx] = { question: qObj.question, answer: text };
 return updated;
 }
 return [...prev, { question: qObj.question, answer: text }];
 });
 setStatus("ready");
 resolve();
 } catch (err) {
 setErrorModal({ open: true, message:"Transcription error:" + (err.message || err) });
 reject(err);
 }
 };

 mr.start();

 responseTimeoutRef.current = setTimeout(() => {
 if (mediaRecorderRef.current && mediaRecorderRef.current.state !=="inactive") {
 try { mediaRecorderRef.current.stop(); } catch {}
 }
 }, MAX_RESPONSE_TIME);
 } catch (err) {
 reject(err);
 }
 });
 };

const requestFeedback = async () => {
 console.log('Requesting feedback with qa:', qa);
 console.log('Questions:', questions);
 setStatus("requesting_feedback");
 try {
 const r = await fetch(`${API_BASE_URL}/ai-services`, {
 method:"POST",
 headers: {"Content-Type":"application/json" },
 body: JSON.stringify({
 op:"generate-feedback",
 type:"hr", // 👈 HR Interview
 formData,
 qa,
 mediaMeta: {
 audioEnabled,
 videoEnabled,
 },
 }),
 });

 const payload = await r.json();
 if (!payload.ok) throw new Error(payload.error ||"Feedback failed");

 const total = questions.length || 0;
 const answered = qa.filter((x) => x && String(x.answer ||"").trim().length > 0).length;
 const completionScore = total > 0 ? Math.round((answered / total) * 100) : 0;
 const fb = payload.feedback;
 const summary = `HR Score: ${completionScore}. ${(fb.detailed ||"").slice(0, 200)}`;
 speak(summary, voiceIndex); // ⬅️ Optional voice feedback

 trackFeatureUsage({
   feature: 'MockInterview',
   technology: 'HR',
   topic: formData.position || 'Behavioral',
   score: completionScore,
   total_questions: total,
   status: 'completed',
   last_action: 'Completed HR Interview'
 });

 navigate("/interview-result", {
 state: {
 score: completionScore,
 strengths: fb.strengths,
 improvements: fb.improvements,
 detailed: fb.detailed,
 nonVerbal: fb.nonVerbal, // ✅ Pass non-verbal traits
 time: formatTime(totalTimeElapsed / 1000),
 total: questions.length,
 answers: qa,
 questions,
 interviewType:"HR",
 },
 });

 setStatus("finished");
 } catch (err) {
 // Offline fallback: compute a simple score and navigate
 const total = questions.length || 0;
 const answered = qa.filter((x) => x && String(x.answer ||"").trim().length > 0).length;
 const score = total > 0 ? Math.round((answered / total) * 100) : 0;
 navigate("/interview-result", {
 state: {
 score,
 strengths: ["Clear communication"],
 improvements: ["Provide concrete examples"],
 detailed:"Auto-generated feedback (offline mode).",
 nonVerbal: ["Maintain eye contact"],
 time: formatTime(totalTimeElapsed / 1000),
 total: questions.length,
 answers: qa,
 questions,
 interviewType:"HR",
 },
 });
 setStatus("finished");
 }
};


 const formatTime = seconds => {
 const h = Math.floor(seconds / 3600);
 const m = String(Math.floor((seconds % 3600) / 60)).padStart(2,"0");
 const s = String(seconds % 60).padStart(2,"0");
 return `${h}:${m}:${s}`;
 };

 return (
 <div className="flex bg-bgMain text-body min-h-screen relative overflow-hidden font-sans">
 <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -mr-48 -mt-48"></div>
 
 {/* Sidebar */}
 <div className="w-72 bg-bgSection p-8 border-r border-accentGray/30 flex flex-col h-screen relative z-10 shadow-2xl">
 <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-secondary to-primary opacity-50"></div>
 
 {isTenantMode && collegeLogo ? (
   <div className="mb-6 flex justify-center">
     <img src={collegeLogo} alt={collegeName} className="h-12 object-contain" />
   </div>
 ) : (
   <h2 className="text-2xl font-black bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent uppercase tracking-tighter mb-10">
     TheSkillGenie
   </h2>
 )}
 
 <div className="space-y-1 mb-8">
 <p className="text-[10px] font-black text-primary uppercase tracking-widest">{isTenantMode ? collegeName : "Interview Mode"}</p>
 <p className="text-body font-extrabold leading-tight">HR & Behavioral</p>
 </div>

 <div className="space-y-1 mb-8">
 <p className="text-[10px] font-black text-muted uppercase tracking-widest">Target Role</p>
 <p className="text-body font-extrabold leading-tight">
 {formData.position || formData.jobDescription ||"Standard HR"}
 </p>
 </div>

 <div className="mb-10">
 <p className="text-[10px] font-black text-muted uppercase tracking-widest mb-3">Session Timer</p>
 <p className="text-4xl font-mono text-primary drop-shadow-[0_0_10px_rgba(34,211,238,0.3)]">{formatTime(totalTimeElapsed / 1000)}</p>
 </div>

 <p className="text-[10px] font-black text-muted uppercase tracking-widest mb-4">Interview Flow</p>
 <div className="grid grid-cols-5 gap-2 overflow-y-auto custom-scrollbar pr-2">
 {questions.map((_, idx) => (
 <button
 key={idx}
 onClick={async () => {
 stopAnswering();
 await new Promise(resolve => setTimeout(resolve, 200));
 window.speechSynthesis.cancel();
 setCurrentIdx(idx);
 setVoiceIndex(prev => prev + 1);
 setStatus("ready");
 askQuestion(idx, questions);
 }}
 className={`w-10 h-10 rounded-xl text-xs font-black transition-all duration-200 ${
 currentIdx === idx
 ?"bg-primary text-white shadow-lg shadow-primary/30 scale-110"
 : qa[idx]?.answer
 ?"bg-primary/20 text-primary border border-primary/30"
 :"bg-bgMain text-muted border border-accentGray/30 hover:border-primary/50"
 }`}
 >
 {idx + 1}
 </button>
 ))}
 </div>
 </div>

      {/* Main Panel */}
      <div className="flex-1 p-8 md:p-12 overflow-y-auto relative z-10">
        {/* Local Video Stream Preview */}
        {videoEnabled && status !== "idle" && (
          <div 
            className="fixed bottom-6 right-6 w-48 md:w-64 aspect-video bg-black rounded-3xl overflow-hidden shadow-2xl border-4 border-primary/20 z-50 animate-fadeIn cursor-move touch-none"
            style={{
              transform: `translate(${videoPos.x}px, ${videoPos.y}px)`,
              transition: isDragging ? 'none' : 'transform 0.1s ease-out'
            }}
            onMouseDown={handleMouseDown}
          >
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              className="w-full h-full object-cover scale-x-[-1] pointer-events-none"
            />
          </div>
        )}

        <div className="flex justify-between items-center mb-10">
 <button
 onClick={() => navigate(backPath)}
 className="flex items-center gap-2 text-muted hover:text-white transition-colors group font-black uppercase text-[10px] tracking-widest"
 >
 <span className="w-8 h-8 rounded-full bg-bgSection flex items-center justify-center group-hover:bg-primary/20 transition-all">←</span>
 End Interview
 </button>
 <div className="px-6 py-2 bg-primary/10 text-primary border border-primary/30 rounded-full text-[10px] font-black uppercase tracking-widest">
 {status.toUpperCase()} MODE
 </div>
 </div>

 <div className="max-w-4xl mx-auto">
 <div className="bg-bgSection p-8 md:p-12 rounded-[48px] shadow-2xl border border-accentGray/30 relative overflow-hidden">
 <div className="absolute -top-24 -right-24 w-64 h-64 bg-primary/10 rounded-full blur-3xl"></div>
 
 {status ==="generating" && (
 <div className="text-center py-20">
 <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-6"></div>
 <p className="text-primary font-black uppercase tracking-widest">Synthesizing HR Scenarios...</p>
 </div>
 )}

 {status ==="idle" && questions.length === 0 && (
 <div className="text-center py-10">
 <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-8 border border-primary/20">
 <span className="text-5xl">👔</span>
 </div>
 <h2 className="text-3xl font-black text-black mb-4 uppercase tracking-tighter">Ready for HR Round?</h2>
 <p className="text-muted mb-10 font-medium">Please review your media settings before initiating the behavioral assessment session.</p>
 
            <div className="flex flex-col items-center gap-8">
              {mediaError && (
                <div className="bg-red-500/10 border border-red-500/50 text-red-500 px-6 py-3 rounded-2xl text-sm font-bold animate-shake">
                  {mediaError}
                </div>
              )}
              <div className="flex gap-10">
                <div className="flex items-center gap-3 opacity-75 cursor-not-allowed">
                  <div className="w-6 h-6 rounded-lg bg-primary border-2 border-primary flex items-center justify-center">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <span className="text-sm font-black uppercase tracking-widest text-body">Audio (Mandatory)</span>
                </div>
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${videoEnabled ? 'bg-secondary border-secondary' : 'border-accentGray/50'}`}>
                    {videoEnabled && <span className="text-white text-xs">✓</span>}
                  </div>
                  <input
                    type="checkbox"
                    checked={videoEnabled}
                    onChange={(e) => setVideoEnabled(e.target.checked)}
                    className="hidden"
                  />
                  <span className="text-sm font-black uppercase tracking-widest text-body group-hover:text-primary">Enable Video</span>
                </label>
              </div>

              <button
                className="btn-primary-gradient px-12 py-4 rounded-2xl font-black text-lg shadow-xl hover:scale-105 active:scale-95 transition-all uppercase tracking-widest"
                onClick={handleLaunch}
                disabled={status === "generating"}
              >
                Start HR Session
              </button>
            </div>
 </div>
 )}

 {questions.length > 0 && status !=="idle" && (
 <div className="relative z-10">
 <div className="flex justify-between items-start mb-10">
 <div className="text-xs font-black text-primary mb-2 uppercase tracking-widest">
 Behavioral Question {currentIdx + 1}
 </div>
 <div className="bg-bgMain px-4 py-1.5 rounded-full border border-accentGray/30 text-[10px] font-black text-primary uppercase tracking-widest">
 ⏱️ {timers[currentIdx] || 0}s elapsed
 </div>
 </div>

 <h2 className="text-2xl md:text-3xl font-extrabold text-black mb-12 leading-relaxed tracking-tight">
 {questions[currentIdx]?.question}
 </h2>

 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12 pt-10 border-t border-accentGray/30">
 <button
 className={`p-4 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all shadow-lg ${
 status ==="ready" 
 ?"bg-green-500 text-black shadow-green-500/20 hover:scale-105 active:scale-95" 
 :"bg-white/5 text-muted border border-accentGray/50 cursor-not-allowed opacity-50"
 }`}
 onClick={startAnswering}
 disabled={status !=="ready"}
 >
 🎤 Start Answer
 </button>
 <button
 className={`p-4 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all shadow-lg ${
 status ==="listening" || status ==="recording"
 ?"bg-primary text-white shadow-primary/30 hover:scale-105 active:scale-95 animate-pulse"
 :"bg-white/5 text-muted border border-accentGray/50 cursor-not-allowed opacity-50"
 }`}
 onClick={stopAnswering}
 disabled={!(status ==="listening" || status ==="recording")}
 >
 ⏹️ Stop & Save
 </button>
 <button
 className="p-4 rounded-2xl bg-bgMain text-muted border border-accentGray/50 font-black text-[10px] uppercase tracking-widest hover:text-white hover:border-secondary transition-all shadow-lg"
 onClick={skipQuestion}
 disabled={status ==="listening" || status ==="recording" || status ==="transcribing"}
 >
 ⏩ Skip Question
 </button>
 <button
 className="btn-primary-gradient p-4 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl hover:scale-105 active:scale-95 transition-all"
 onClick={requestFeedback}
 disabled={status ==="listening" || status ==="recording" || status ==="transcribing"}
 >
 🏁 Finish Interview
 </button>
 </div>
 </div>
 )}
 </div>

 {/* Navigation buttons */}
 {questions.length > 0 && (
 <div className="flex justify-between mt-8">
 <button
 className="bg-bgSection text-muted border border-accentGray/30 px-8 py-3 rounded-2xl font-black uppercase text-[10px] tracking-widest hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed"
 onClick={async () => {
 stopAnswering();
 await new Promise(resolve => setTimeout(resolve, 200));
 const prev = currentIdx - 1;
 if (prev >= 0) {
 setCurrentIdx(prev);
 setVoiceIndex(prev => prev + 1);
 setStatus("ready");
 askQuestion(prev, questions);
 }
 }}
 disabled={currentIdx === 0}
 >
 ← Previous
 </button>

 <button
 className="bg-bgSection text-muted border border-accentGray/30 px-8 py-3 rounded-2xl font-black uppercase text-[10px] tracking-widest hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed"
 onClick={async () => {
 stopAnswering();
 await new Promise(resolve => setTimeout(resolve, 200));
 const next = currentIdx + 1;
 if (next < questions.length) {
 setCurrentIdx(next);
 setVoiceIndex(prev => prev + 1);
 setStatus("ready");
 askQuestion(next, questions);
 }
 }}
 disabled={currentIdx === questions.length - 1}
 >
 Next →
 </button>
 </div>
 )}
 </div>
 </div>

 {/* Error Modal */}
 {errorModal.open && (
 <div className="fixed inset-0 bg-bgMain/90 backdrop-blur-md flex items-center justify-center z-[100] p-6">
 <div className="bg-bgSection border border-red-500/30 rounded-[32px] p-10 max-w-md w-full text-center shadow-2xl">
 <h2 className="text-2xl font-black text-red-400 mb-4 uppercase tracking-widest">System Error</h2>
 <p className="text-body/80 mb-10 leading-relaxed">{errorModal.message}</p>
 <button
 className="w-full bg-red-500 text-white py-4 rounded-2xl font-black uppercase tracking-widest hover:bg-red-600 transition-all shadow-xl"
 onClick={() => setErrorModal({ open: false, message:"" })}
 >
 Okay
 </button>
 </div>
 </div>
 )}
 </div>
 );
}


